import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import SaveIcon from '@mui/icons-material/Save'
import { Box, Button, Grid } from '@mui/material'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { sortCourses } from '../../../../../redux/actions/courses'
import Header from '../../../../shared/components/Header'
import ButtonBack from '../../../../shared/components/buttons/ButtonBack'
import SubHeader from '../../../../shared/styles/Subheader'
import { Courses } from '../../models/course'
import DraggableCourseItem from './DraggableCourseItem'

interface DraggableCourseGridProps {
  courses: Courses[]
  isMobile: boolean
  isTablet: boolean
}

const DraggableCourseGrid = ({ courses = [], isMobile, isTablet }: DraggableCourseGridProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['global'])
  const [items, setItems] = useState<Courses[]>([])
  const [order, setOrder] = useState<{ sortedCourses: { id: number; order: number }[] }>({ sortedCourses: [] })

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { delay: 100, tolerance: 5 } }), useSensor(KeyboardSensor))
  const itemIds = useMemo(() => items.map(item => item.id), [items])

  useEffect(() => {
    if (!courses || courses.length === 0) return
    setItems(courses)
  }, [courses])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    setItems(prevItems => {
      const oldIndex = prevItems.findIndex(item => item.id === active.id)
      const newIndex = prevItems.findIndex(item => item.id === over.id)
      const newItems = arrayMove(prevItems, oldIndex, newIndex)

      const newOrder = {
        sortedCourses: newItems.map((item, index) => ({
          id: item.id,
          order: index + 1
        }))
      }
      setOrder(newOrder)

      return newItems
    })
  }, [])

  const handleSaveOrder = useCallback(() => {
    if (order.sortedCourses.length > 0) {
      dispatch(sortCourses(order))
    }
  }, [dispatch, order])

  if (!courses || courses.length === 0) {
    return null
  }

  return (
    <>
      <Header title={t('management-courses')} />
      <SubHeader />
      <Box display="flex" pl={2}>
        <ButtonBack goBack />
        <Box mt={7} mb={6} ml="auto">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveOrder}
            endIcon={<SaveIcon />}
            disabled={order.sortedCourses.length === 0}
          >
            {t('save-order')}
          </Button>
        </Box>
      </Box>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={itemIds} strategy={horizontalListSortingStrategy}>
          <Grid container spacing={isMobile ? 1 : 2} direction="row" justifyContent="center">
            {items.map(course => (
              <DraggableCourseItem key={course.id} course={course} isMobile={isMobile} isTablet={isTablet} />
            ))}
          </Grid>
        </SortableContext>
      </DndContext>
    </>
  )
}

export default memo(DraggableCourseGrid)
